// The seven symmetric coin-economy KPIs.
//
// Source of truth: Bvndle_Coin_KPI_Structure_with_preamble.md. Three supply
// KPIs (S1-S3, issuance), three demand KPIs (D1-D3, redemption), mirrored so
// asymmetry is visible at a glance, plus SYM — the symmetry read — as the
// hero. Every KPI carries the five-field accountability shape Nonso asked for:
// value indicators · owner · how we track · blockers · what we're doing.
//
// Owners follow the "primary · supporting" convention: the lead name carries
// the card; supporting owners are noted in smaller weight after the middot.
// Mapping is anchored to the Bvndle Daily Standup Action Tracker (latest sheet
// 20.5.26) + the UBA/MTN trackers + the Partner Ecosystem 2-pager (12 May 2026).
//
// Tracking / blockers / actions are pulled from the latest tracker entries.
// This card is the strategic ManCo lens — granular line-items live in the
// Action Tracker; this surface elevates only what matters for symmetry.
//
// Each value indicator may bind to a Metabase-backed query-catalog entry
// (queryId) so the headline number is live. Where reconciliation primitives
// aren't live yet, `placeholder` carries the current strategic status from
// the partner ecosystem snapshot rather than the literal string "awaiting".

export type KpiSide = "supply" | "demand" | "sym";

export type KpiStatus =
  | "good"
  | "on-track"
  | "ahead"
  | "watch"
  | "warn"
  | "blocked"
  | "awaiting";

/** How to reduce a query result's rows to one headline number/label. */
export type Reducer = "firstRowValue" | "lastRowValue" | "sumValue" | "rowCount" | "topLabel";

/** Semantic unit so the UI can distinguish loyalty coins from cash. */
export type ValueUnit = "coins" | "currency" | "count" | "ratio";

export interface ValueIndicator {
  /** Human label for the metric. */
  label: string;
  /**
   * Query-catalog entry id to bind to (see resources/cohorts/query-catalog
   * .json). When set and the query runs live, the headline shows the reduced
   * value; otherwise it shows `placeholder` with an "awaiting query" tag.
   */
  queryId?: string;
  reducer?: Reducer;
  /**
   * Plain text suffix appended to numeric headlines (e.g. "redemptions/yr").
   * Kept for backward compatibility; prefer `unit` for coin/currency so the
   * UI can render a chip and distinguish loyalty units from cash.
   */
  suffix?: string;
  /** Semantic unit for chip rendering. When set, the suffix is NOT appended. */
  unit?: ValueUnit;
  /** Shown when there's no bound query yet. */
  placeholder?: string;
}

/**
 * Which per-category metric this KPI surfaces in the must-win-categories
 * column. Same 9 rows under every block, different number per row per block —
 * that's how the blocks earn their independence (same x-axis, different y).
 *
 *   first365d        D1 — where redemption is actually happening (volume)
 *   retain14dPct     D2 — habit formation (does the redeem turn into return?)
 *   retainSameDayPct D2 alt — fast-habit signal
 *   everReturnedPct  recurrence baseline
 *   effortCount      S1 / D1 — committed actions tagged to the category
 *   fit              the derived pattern-fit score
 */
export type CategoryMetricKey =
  | "first365d"
  | "retain14dPct"
  | "retainSameDayPct"
  | "everReturnedPct"
  | "effortCount"
  | "fit";

export interface Kpi {
  id: string;
  code: string; // "S1" | "D1" | "SYM" ...
  side: KpiSide;
  title: string;
  status: KpiStatus;
  /** The five-field accountability shape. */
  valueIndicators: ValueIndicator[];
  owner: string;
  tracking: string;
  blockers: string;
  actions: string;
  /** The question this KPI block is asking. Surfaced as a tagline. */
  question?: string;
  /**
   * Strategic call this block makes — the inline intelligence layer. Each
   * block earns its place by interpreting its own data; this is what the
   * ManCo argues with, not generic copy.
   */
  strategicCall?: string;
  /**
   * Which per-category metric to show in the must-win-categories column for
   * this KPI. Different per block — that's the point.
   */
  categoryMetricKey?: CategoryMetricKey;
  /** Label for the per-category metric column header on this block. */
  categoryMetricLabel?: string;
}

export interface KpiBundle {
  title: string;
  subtitle: string;
  pulled_at: string;
  kpis: Kpi[];
}

// ---------------------------------------------------------------------------

export const KPI_BUNDLE: KpiBundle = {
  title: "Bvndle Coin Economy · Command Centre",
  subtitle:
    "A healthy coin economy is measured by symmetry between issuance and redemption over time, not absolute volume. Three supply KPIs and three demand KPIs answer the same three questions · coverage, attractiveness, cost · so asymmetry shows at a glance.",
  pulled_at: "2026-05-25",
  kpis: [
    // ===================== SYM — the hero =====================
    {
      id: "sym",
      code: "SYM",
      side: "sym",
      title: "Is the coin economy in balance?",
      status: "awaiting",
      valueIndicators: [
        {
          label: "S1 / D1 coverage ratio (≈ 1 is balanced)",
          placeholder: "awaiting coverage trackers",
        },
        {
          label: "Months of float (outstanding liability ÷ monthly settlement)",
          placeholder: "awaiting ledger reconciliation",
        },
        {
          label: "Cross-category redemption ratio",
          queryId: "redeem-category-share",
          reducer: "rowCount",
          suffix: "categories",
        },
      ],
      owner: "Kabelo · Ikechukwu (ecosystem oversight)",
      tracking:
        "Where earn lands vs where spend lands, per category. The asymmetry between the two is the strategic signal · single-leg loops accumulate liability or starve demand.",
      blockers:
        "Reconciliation + settlement primitives not yet live; supply heavily concentrated in signup_bonus; spend heavily concentrated in fee-defense.",
      actions:
        "Land earn channels in the 5–6 must-win categories; close the loop locally per category; flag asymmetries as they emerge.",
      question: "Where is earn matched to spend · and where do they diverge?",
    },

    // ===================== SUPPLY — issuance =====================
    {
      id: "s1",
      code: "S1",
      side: "supply",
      title: "Must-win partners onboarded · issuance",
      status: "watch",
      valueIndicators: [
        {
          label: "Coins issued by category · 90d",
          queryId: "merchant-coin-issue",
          reducer: "sumValue",
          unit: "coins",
        },
        { label: "Weekly delta in must-win partners signed", placeholder: "awaiting pipeline tracker" },
        { label: "Signed-to-live conversion rate", placeholder: "awaiting pipeline tracker" },
      ],
      owner: "Seye · Ikechukwu (ecosystem)",
      tracking:
        "LaaS sub-tracker + UBA/MTN per-client trackers. ManCo lens: count live banks, not pipeline noise. BRF funding plan (Elmer) feeds the issuer-of-issuers tier.",
      blockers:
        "Zenith/Tatum/WEMA stuck at top-of-house decisions; commercial revisions awaited at WEMA; PS vendor delivering Airtime-only (not Data) for MTN's bill-payments removal.",
      actions:
        "Drive Fri 24th unblock meetings (Lotus, AXA); Zuby/FJ engaging Zenith top management; reposition Tatum ask via Ufuoma; close eDryv redemption + SSO demo (27/4); BRF funding-plan meeting reset.",
      question: "Where is issuance happening · and where do we want it?",
      // No strategicCall — the distribution table below IS the strategic
      // argument, row-by-row. Each row becomes a ManCo commitment record.
      categoryMetricKey: "effortCount",
      categoryMetricLabel: "EFFORT (ACTIONS)",
    },
    {
      id: "s2",
      code: "S2",
      side: "supply",
      title: "Is issuing compelling to customers?",
      status: "on-track",
      valueIndicators: [
        {
          label: "Coins issued by category (90d, effective issuance proxy)",
          queryId: "merchant-coin-issue",
          reducer: "sumValue",
          unit: "coins",
        },
        { label: "Time-to-first-meaningful-redemption at average spend", placeholder: "awaiting cohort query" },
        { label: "Customer worth-it score (in-app survey)", placeholder: "awaiting survey backend" },
      ],
      owner: "Ufuoma · Semilore (Akoni) on data",
      tracking: "Earn-event frequency per active user; % of active users at fiat-useful threshold per category (computable today from coinsLedger). Gift-Card Phase 1 GTM (Workers Day, May 1) is the live issuance test.",
      blockers:
        "Single earn channel today (98% signup_bonus) · no recurring earn for most users; per-category thresholds gated on partner-issuer integrations; classifier missing for Transport / Groceries / Pharmacies.",
      actions:
        "Land partner-issuer earn paths per category (start with MTN data, UBA card, Medplus); turn on BvndleGO classifier so transport earn becomes visible; use Gaming as a payday multiplier on the categories already turned on.",
      question: "Are users earning often enough · and does it add up to something useful?",
      // Distribution chart below carries the row-level argument and the
      // commitment lifecycle. No abstract strategic-call paragraph.
    },
    {
      id: "s3",
      code: "S3",
      side: "supply",
      title: "What does it cost to issue?",
      status: "watch",
      valueIndicators: [
        {
          label: "Annualised redemption volume (settlement proxy)",
          queryId: "annual-redemption-volume",
          reducer: "firstRowValue",
          suffix: "redemptions/yr",
        },
        { label: "Outstanding coin liability per partner", unit: "coins", placeholder: "awaiting ledger reconciliation" },
        { label: "Liability vs pre-agreed cap", unit: "ratio", placeholder: "awaiting ledger reconciliation" },
      ],
      owner: "Kabelo · Ikechukwu (commercial)",
      tracking: "Annual coin issuance × ₦10 = liability on the books. The split between Bvndle-funded and partner-consigned issuance is the lever.",
      blockers:
        "Today every coin issued sits on Bvndle's balance sheet. No partner consignment agreements signed yet. Marketplace SOP (how items are costed) not yet documented.",
      actions:
        "Negotiate consignment agreements per category (UBA for Card, MTN/Glo for Mobile, NEPA/DSTV for Bills, Medplus for Pharmacies). Document Marketplace SOP. Set per-partner liability caps at onboarding.",
      question: "What's it costing us · and who's funding it?",
    },

    // ===================== DEMAND — redemption =====================
    {
      id: "d1",
      code: "D1",
      side: "demand",
      title: "Must-win partners onboarded · redemption",
      status: "warn",
      valueIndicators: [
        {
          label: "Redemption coverage per category (where coins are spent)",
          queryId: "redeem-category-share",
          reducer: "rowCount",
          suffix: "categories",
        },
        { label: "Concentration risk · top-2 partner share", placeholder: "Yard + GARA-MART = 64.5%" },
        { label: "Long-tail partners (≤2 items)", placeholder: "11 of 25" },
      ],
      owner: "Ufuoma · Williams (merchants), Chibuzor (Yard)",
      tracking: "Merchant List Run-through (daily, Williams) + Yard checklist revamp (Chibuzor, due 22 Apr) + Pop-up framework + random store-test feedback loop (All Team).",
      blockers:
        "Top-2 partner concentration at 64.5% of inventory; Luxury Retail single-anchored (Yard); 11 partners on ≤2 items; delivery price-awareness blocked at Movis (no API); Phase 2 Physical Gift Cards PRD outstanding.",
      actions:
        "Yard checklist revamp + Pop-up framework (Chibuzor); Williams publishes live merchant list for ManCo store-tests; ecosystem LaaS+Redemption plan closeout (Ikechukwu); eDryv redemption demo (27/4); regional expansion into other states queued behind UBA Phase-2.",
      question: "Where is redemption actually happening?",
      strategicCall:
        "73% fee-defense · 14% airtime · 4% data · <1% utilities. Three of four first-redeems are fee-defense, not merchant. Strategic effort on Yard (Luxury, weak fit) and Cosmetics (5 partners, weak fit) may be misallocated against the wedge shape · Groceries / Utilities / BvndleGO are under-prosecuted.",
      categoryMetricKey: "first365d",
      categoryMetricLabel: "REDEMPTION VOLUME",
    },
    {
      id: "d2",
      code: "D2",
      side: "demand",
      title: "Is redeeming compelling to customers?",
      status: "good",
      valueIndicators: [
        {
          label: "Habit window · return latency to 2nd redeem",
          queryId: "habit-window-distribution",
          reducer: "sumValue",
          suffix: "returners",
        },
        { label: "Friction step count, app-open → coin-applied", placeholder: "awaiting funnel instrumentation" },
        { label: "Worth-it score (redemption module)", placeholder: "awaiting survey backend" },
      ],
      owner: "Ibinye · Ruki (BvndleGO redemption)",
      tracking: "% of users who return within 14 days of first redemption per category (the habit window). 50% of all returners come back ≤14 days · that's the lifecycle leverage point.",
      blockers:
        "Delivery price-awareness blocked at Movis (no API); voucher-code UX vs tap-to-redeem split; Transport/Groceries/Pharmacies not yet instrumented.",
      actions:
        "Standardise redemption SDK; steer first-redeemers toward retentive categories (Card, Gift Card, Data); merchant-led delivery scoping (Lateef + Seye).",
      question: "Are users redeeming often enough · and does it form a habit?",
    },
    {
      id: "d3",
      code: "D3",
      side: "demand",
      title: "What does it cost to redeem?",
      status: "on-track",
      valueIndicators: [
        {
          label: "Settlement velocity · annualised redemption volume",
          queryId: "annual-redemption-volume",
          reducer: "firstRowValue",
          suffix: "redemptions/yr",
        },
        { label: "Redemption partner OPEX index (trended)", placeholder: "awaiting OPEX consolidation" },
        { label: "Cash-out latency to partner", placeholder: "awaiting settlement file" },
      ],
      owner: "Kabelo (settlement rails)",
      tracking: "Settlement latency per category × redemption volume = working-capital cost. Instant rails (fee-defense, mobile top-up) keep cost down; merchant rails add latency.",
      blockers:
        "Settlement latency expectations vary by partner (instant vs T+7); merchant disputes resolved manually; Transport/Groceries/Pharmacies rails not yet defined.",
      actions:
        "Tiered settlement SLA by partner size; same-rail settlement via Peppermint when ready; dispute automation behind the ledger audit log.",
      question: "How fast does each channel settle · and what does latency cost us?",
    },
  ],
};

export const SUPPLY_KPIS = KPI_BUNDLE.kpis.filter((k) => k.side === "supply");
export const DEMAND_KPIS = KPI_BUNDLE.kpis.filter((k) => k.side === "demand");
export const SYM_KPI = KPI_BUNDLE.kpis.find((k) => k.side === "sym")!;

/**
 * What the work surfaces render: just the side's KPIs. SYM is a diagnostic
 * roll-up — it lives on /about under the philosophy write-up, not in front
 * of the working team. The first thing the team sees on login is the
 * actionable view: a number, who's responsible, what to do about it.
 */
export const SUPPLY_SURFACE_KPIS = SUPPLY_KPIS;
export const DEMAND_SURFACE_KPIS = DEMAND_KPIS;

export const STATUS_COLOR: Record<KpiStatus, string> = {
  good: "#5ec98c",
  "on-track": "#5ec98c",
  ahead: "#5ec98c",
  watch: "var(--amber)",
  warn: "var(--amber)",
  blocked: "var(--coral)",
  awaiting: "var(--ink-faint)",
};

export const STATUS_LABEL: Record<KpiStatus, string> = {
  good: "GOOD",
  "on-track": "ON TRACK",
  ahead: "AHEAD",
  watch: "WATCH",
  warn: "WARN",
  blocked: "BLOCKED",
  awaiting: "AWAITING DATA",
};
